import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Link,
  useTheme,
  alpha,
  Fade,
  Grow,
  Zoom,
  keyframes
} from '@mui/material';
import {
  Security as SecurityIcon,
  Code as CodeIcon,
  Policy as PolicyIcon,
  Speed as SpeedIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  GitHub as GitHubIcon,
  Description as DescriptionIcon,
  Architecture as ArchitectureIcon,
  Shield as ShieldIcon,
  BugReport as BugReportIcon,
  Verified as VerifiedIcon,
  Bolt as BoltIcon,
  CloudOff as CloudOffIcon,
  IntegrationInstructions as IntegrationIcon,
  Gavel as GavelIcon,
  Terminal as TerminalIcon,
  RocketLaunch as RocketIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Keyframe animations
const pulse = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(0, 242, 96, 0.3); }
  50% { box-shadow: 0 0 40px rgba(0, 242, 96, 0.6), 0 0 60px rgba(5, 117, 230, 0.3); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const typewriter = keyframes`
  from { width: 0; }
  to { width: 100%; }
`;

const DevSecOpsPipeline: React.FC = () => {
  const theme = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const features = [
    {
      icon: <ShieldIcon sx={{ fontSize: 48 }} />,
      title: 'Multi-Agent AI',
      description: '3 specialized AI agents analyze every code change for security, quality, and policy compliance',
      gradient: 'linear-gradient(135deg, #00f260 0%, #0575e6 100%)'
    },
    {
      icon: <BoltIcon sx={{ fontSize: 48 }} />,
      title: 'Lightning Fast',
      description: 'Parallel execution completes in ~5 seconds with local MLX on Apple Silicon',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: <ErrorIcon sx={{ fontSize: 48 }} />,
      title: 'Auto-Blocking',
      description: 'Automatically prevents risky code from reaching production with pre-push hooks',
      gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee0979 100%)'
    },
    {
      icon: <CloudOffIcon sx={{ fontSize: 48 }} />,
      title: 'Zero Cloud Costs',
      description: 'Runs entirely on local hardware with MLX - no API keys or cloud fees',
      gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)'
    },
    {
      icon: <IntegrationIcon sx={{ fontSize: 48 }} />,
      title: 'Git Native',
      description: 'Seamless workflow via pre-commit and pre-push hooks - zero friction',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: <GavelIcon sx={{ fontSize: 48 }} />,
      title: 'Policy Enforcement',
      description: 'Enforce team rules: tests required, no secrets, auth on new endpoints',
      gradient: 'linear-gradient(135deg, #f2994a 0%, #f2c94c 100%)'
    }
  ];

  const agents = [
    {
      name: 'CodeReview Agent',
      icon: <BugReportIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, #00f260 0%, #0575e6 100%)',
      emoji: '🔍',
      checks: [
        'Code quality and maintainability',
        'Bugs and correctness issues',
        'Style and best practices',
        'Suggests concrete fixes'
      ]
    },
    {
      name: 'Security Agent',
      icon: <ShieldIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)',
      emoji: '🛡️',
      checks: [
        'SQL injection detection',
        'XSS vulnerabilities',
        'Hardcoded secrets / API keys',
        'Weak crypto, dangerous functions',
        'CWE/CVE references'
      ]
    },
    {
      name: 'Policy Agent',
      icon: <VerifiedIcon sx={{ fontSize: 32 }} />,
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      emoji: '📋',
      checks: [
        'Tests must change with production code',
        'No obvious secrets in commits',
        'New endpoints require authentication'
      ]
    }
  ];

  const healthItems = [
    { name: 'Pre-commit Hook', status: 'operational' },
    { name: 'Pre-push Hook', status: 'operational' },
    { name: 'Code Review Agent', status: 'operational' },
    { name: 'Security Agent', status: 'operational' },
    { name: 'Policy Agent', status: 'operational' },
    { name: 'MLX Server', status: 'operational' },
    { name: 'GitHub Integration', status: 'operational' }
  ];

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'operational':
        return <CheckCircleIcon sx={{ color: '#00b09b', fontSize: 20 }} />;
      case 'warning':
        return <WarningIcon sx={{ color: '#f9a825', fontSize: 20 }} />;
      case 'error':
        return <ErrorIcon sx={{ color: '#ff6b6b', fontSize: 20 }} />;
      default:
        return <CheckCircleIcon sx={{ color: '#00b09b', fontSize: 20 }} />;
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha('#0f0c29', 0.95)} 0%, ${alpha('#302b63', 0.9)} 50%, ${alpha('#24243e', 0.95)} 100%)`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(0, 242, 96, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(5, 117, 230, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6, position: 'relative', zIndex: 1 }}>
        {/* Hero Section - Stunning */}
        <Fade in={mounted} timeout={600}>
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8, 
              mb: 6,
              position: 'relative'
            }}
          >
            {/* Animated background glow */}
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 400,
                height: 400,
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(0, 242, 96, 0.15) 0%, transparent 70%)',
                filter: 'blur(60px)',
                animation: `${pulse} 4s ease-in-out infinite`
              }}
            />
            
            {/* Shield Icon */}
            <Zoom in={mounted} timeout={800}>
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #00f260 0%, #0575e6 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 'auto',
                  mb: 4,
                  boxShadow: '0 20px 60px rgba(0, 242, 96, 0.4)',
                  animation: `${glow} 3s ease-in-out infinite, ${float} 4s ease-in-out infinite`
                }}
              >
                <ShieldIcon sx={{ fontSize: 60, color: 'white' }} />
              </Box>
            </Zoom>

            <Typography
              variant="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2.5rem', md: '4rem' },
                background: 'linear-gradient(135deg, #00f260 0%, #0575e6 50%, #f093fb 100%)',
                backgroundSize: '200% auto',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                animation: `${shimmer} 3s linear infinite`,
                mb: 3,
                textShadow: '0 0 80px rgba(0, 242, 96, 0.3)'
              }}
            >
              Agentic DevSecOps Pipeline
            </Typography>
            
            <Typography 
              variant="h5" 
              sx={{ 
                mb: 4, 
                maxWidth: 800, 
                mx: 'auto',
                color: alpha('#fff', 0.8),
                fontWeight: 300,
                lineHeight: 1.8
              }}
            >
              AI-powered code review and security analysis for every commit and push.
              <br />
              <strong style={{ color: '#00f260' }}>Block vulnerabilities before they reach production.</strong>
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircleIcon />}
                label="OPERATIONAL"
                sx={{
                  background: 'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 3,
                  py: 2.5,
                  fontSize: '1rem',
                  borderRadius: 3,
                  boxShadow: '0 8px 25px rgba(0, 176, 155, 0.4)',
                  animation: `${pulse} 2s ease-in-out infinite`,
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
              <Chip
                icon={<RocketIcon />}
                label="DEPLOYED TO E-UNIFY"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: 'bold',
                  px: 3,
                  py: 2.5,
                  fontSize: '1rem',
                  borderRadius: 3,
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  '& .MuiChip-icon': { color: 'white' }
                }}
              />
            </Box>
          </Box>
        </Fade>

      {/* System Architecture */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: alpha(theme.palette.background.paper, 0.8) }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#00b09b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArchitectureIcon /> System Architecture
        </Typography>
        <Box
          component="pre"
          sx={{
            background: alpha(theme.palette.common.black, 0.3),
            p: 3,
            borderRadius: 2,
            fontFamily: 'monospace',
            fontSize: '0.85rem',
            overflow: 'auto',
            borderLeft: '4px solid #00b09b',
            lineHeight: 1.6
          }}
        >
{`┌─────────────────────────────────────────────────────────┐
│                    Git Workflow                          │
├─────────────────────────────────────────────────────────┤
│  git commit → Pre-commit Hook (quick scan)              │
│  git push   → Pre-push Hook (full AI analysis)         │
└─────────────────────────────────────────────────────────┘
                          │
                          ▼
┌─────────────────────────────────────────────────────────┐
│                AI Agent Analysis                         │
├─────────────────────────────────────────────────────────┤
│  ┌─────────────┐ ┌─────────────┐ ┌─────────────┐       │
│  │ CodeReview  │ │  Security   │ │   Policy    │       │
│  │   Agent     │ │   Agent     │ │   Agent     │       │
│  └─────────────┘ └─────────────┘ └─────────────┘       │
│        │                │                │              │
│        ▼                ▼                ▼              │
│   Code quality    Vulnerabilities    Rules check       │
│   Bugs, style     SQL injection      Tests updated?    │
│   Best practices  Hardcoded keys   Secrets?           │
└─────────────────────────────────────────────────────────┘
                          │
                          ▼
┌─────────────────────────────────────────────────────────┐
│                   Decision Engine                        │
├─────────────────────────────────────────────────────────┤
│  🔴 BLOCK if: High security risk OR policy fail         │
│  🟡 WARN if:  Medium code review issues                 │
│  🟢 PASS if:  All clear                                 │
└─────────────────────────────────────────────────────────┘`}
        </Box>
      </Paper>

      {/* AI Agents - Stunning Cards */}
      <Box sx={{ mb: 6 }}>
        <Fade in={mounted} timeout={800}>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 4, 
              textAlign: 'center',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #00f260 0%, #0575e6 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            🤖 AI Agents
          </Typography>
        </Fade>
        <Grid container spacing={4}>
          {agents.map((agent, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Grow in={mounted} timeout={600 + index * 200}>
                <Card
                  sx={{
                    height: '100%',
                    background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.95)} 100%)`,
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-12px) scale(1.02)',
                      boxShadow: `0 20px 40px ${alpha('#000', 0.3)}`,
                      '& .agent-header': {
                        transform: 'scale(1.05)'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '6px',
                      background: agent.gradient
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box 
                      className="agent-header"
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 2, 
                        mb: 3,
                        transition: 'transform 0.3s ease'
                      }}
                    >
                      <Box
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 3,
                          background: agent.gradient,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          boxShadow: `0 8px 20px ${alpha('#000', 0.2)}`,
                          animation: `${float} 3s ease-in-out infinite`,
                          animationDelay: `${index * 0.2}s`
                        }}
                      >
                        {agent.icon}
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'text.primary' }}>
                          {agent.emoji} {agent.name}
                        </Typography>
                      </Box>
                    </Box>
                    <Box component="ul" sx={{ pl: 0, m: 0, listStyle: 'none' }}>
                      {agent.checks.map((check, i) => (
                        <Box
                          key={i}
                          component="li"
                          sx={{ 
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            background: alpha(theme.palette.primary.main, 0.05),
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              background: alpha(theme.palette.primary.main, 0.1),
                              transform: 'translateX(8px)'
                            }
                          }}
                        >
                          <CheckCircleIcon sx={{ fontSize: 18, color: '#00f260' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {check}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grow>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Key Features - Glassmorphism Cards */}
      <Box sx={{ mb: 6 }}>
        <Fade in={mounted} timeout={1000}>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 4, 
              textAlign: 'center',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            ⚡ Key Features
          </Typography>
        </Fade>
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Zoom in={mounted} timeout={400 + index * 100}>
                <Card
                  sx={{
                    height: '100%',
                    background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.7)} 0%, ${alpha(theme.palette.background.default, 0.5)} 100%)`,
                    backdropFilter: 'blur(20px)',
                    borderRadius: 4,
                    border: `1px solid ${alpha('#fff', 0.1)}`,
                    overflow: 'hidden',
                    position: 'relative',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    '&:hover': {
                      transform: 'translateY(-8px) rotateX(5deg)',
                      boxShadow: `0 25px 50px ${alpha('#000', 0.25)}`,
                      '& .feature-icon': {
                        transform: 'scale(1.2) rotate(10deg)',
                        boxShadow: `0 15px 30px ${alpha('#000', 0.3)}`
                      }
                    }
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      className="feature-icon"
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: feature.gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        mx: 'auto',
                        mb: 3,
                        boxShadow: `0 10px 25px ${alpha('#000', 0.2)}`,
                        transition: 'all 0.4s ease'
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        fontWeight: 700,
                        background: feature.gradient,
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent'
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Pipeline Health */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: alpha(theme.palette.background.paper, 0.8) }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#00b09b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon /> Pipeline Health
        </Typography>
        <Grid container spacing={2}>
          {healthItems.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 2,
                  background: alpha('#00b09b', 0.1),
                  borderRadius: 2
                }}
              >
                {getStatusIcon(item.status)}
                <Typography variant="body2" fontWeight={500}>
                  {item.name}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* How It Works */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: alpha(theme.palette.background.paper, 0.8) }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#00b09b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon /> How It Works
        </Typography>
        <Box
          component="pre"
          sx={{
            background: alpha(theme.palette.common.black, 0.4),
            p: 3,
            borderRadius: 2,
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflow: 'auto',
            borderLeft: '4px solid #0575e6',
            lineHeight: 1.6
          }}
        >
{`# Step 1: Make code changes
git add .

# Step 2: Commit triggers quick scan
git commit -m "Add feature"
# → Pre-commit: "⚠️ WARNING: Possible secrets detected!"

# Step 3: Push triggers full AI analysis
git push origin main
# → Pre-push: 3 agents analyze diff
# → Block if HIGH risk detected`}
        </Box>
      </Paper>

      {/* Documentation Links */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: alpha(theme.palette.background.paper, 0.8) }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#00b09b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon /> Documentation
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Resource</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Link</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>README</TableCell>
                <TableCell>Quick start guide and usage instructions</TableCell>
                <TableCell>
                  <Link
                    href="https://github.com/rbriehl/E-Unify/blob/main/agentic-devsecops/README.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#0575e6', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <DescriptionIcon fontSize="small" /> View →
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Architecture</TableCell>
                <TableCell>Technical details, system diagrams, agent prompts</TableCell>
                <TableCell>
                  <Link
                    href="https://github.com/rbriehl/E-Unify/blob/main/agentic-devsecops/ARCHITECTURE.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#0575e6', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <ArchitectureIcon fontSize="small" /> View →
                  </Link>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>GitHub Repo</TableCell>
                <TableCell>E-Unify source code and pipeline</TableCell>
                <TableCell>
                  <Link
                    href="https://github.com/rbriehl/E-Unify"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: '#0575e6', display: 'flex', alignItems: 'center', gap: 0.5 }}
                  >
                    <GitHubIcon fontSize="small" /> github.com/rbriehl/E-Unify →
                  </Link>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Configuration */}
      <Paper sx={{ p: 4, mb: 4, borderRadius: 3, background: alpha(theme.palette.background.paper, 0.8) }}>
        <Typography variant="h4" sx={{ mb: 3, color: '#00b09b', display: 'flex', alignItems: 'center', gap: 1 }}>
          <CodeIcon /> Configuration
        </Typography>
        <Box
          component="pre"
          sx={{
            background: alpha(theme.palette.common.black, 0.4),
            p: 3,
            borderRadius: 2,
            fontFamily: 'monospace',
            fontSize: '0.9rem',
            overflow: 'auto',
            borderLeft: '4px solid #0575e6',
            lineHeight: 1.6
          }}
        >
{`# MLX Server (default)
MLX_BASE_URL=http://localhost:8080/v1
MLX_MODEL=mlx-community/Llama-3.2-3B-Instruct-4bit

# Infrastructure Stack:
# - MLX Server on port 8000 (local AI)
# - CORS Proxy on port 8080
# - Agents connect via http://localhost:8080/v1`}
        </Box>
      </Paper>

      {/* Footer */}
      <Fade in={mounted} timeout={1200}>
        <Box 
          sx={{ 
            textAlign: 'center', 
            pt: 6, 
            pb: 4, 
            mt: 4,
            borderTop: `1px solid ${alpha('#fff', 0.1)}`
          }}
        >
          <Typography variant="body1" sx={{ color: alpha('#fff', 0.6), mb: 1 }}>
            🛡️ Agentic DevSecOps Pipeline | Deployed March 29, 2026
          </Typography>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.4) }}>
            E-Unify Project | Powered by MLX on Apple Silicon
          </Typography>
        </Box>
      </Fade>
      </Container>
    </Box>
  );
};

export default DevSecOpsPipeline;
